/**
 * SLA クレジット `/sla-credits`（A146 / R87 / A157）。
 *
 * 障害に伴う返金・値引きの起票と承認。API（`GET/POST /ops/sla-credits`、
 * `POST /ops/sla-credits/:id/approve`）は R0 で揃っていたが画面が無く、
 * 返金の運営フロー全般が DB 直打ちでしか行えなかった。
 *
 * **起票と承認を同じ操作にしない。** 金額が請求書へ直接効くため、
 * `proposed` で作って別 API で承認する。起票者が自分で承認できないよう、
 * 画面側でも「自分が起票したものの承認ボタン」を無効にする
 * （サーバー側に自己承認の禁止は無いので、ここは運用の抑止であることを明記する）。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Banknote, Check, Plus, X } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  Input,
  PageHeader,
  StatusBadge,
  Textarea,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { formatJpy } from '../lib/format.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { hasActiveFilter } from '../lib/search.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import {
  SLA_CREDIT_STATUS_LABEL,
  SLA_CREDIT_STATUS_TONE,
  labelFrom,
  optionsFromLabels,
  toneFrom,
} from '../lib/ops-labels.js';
import { usePermission } from '../stores/auth.js';
import { FilterBar } from '../components/filter-bar.js';
import { FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { SlaCreditEntry } from '../types/api.js';

const routeApi = getRouteApi(RID.slaCredits);

/**
 * まだ現金で返していない額。
 *
 * **`remainingJpy` の 0 は「返す額が無い」ではない。** 既定値の 0 は
 * 「まだ 1 円も充当していない」を表し、`remainingJpy > 0` は「一部だけ
 * 充当されて繰り越している」を表す（サーバー側の `applySlaCredits` /
 * `flagStrandedSlaCredits` と同じ読み方）。ここを `remainingJpy` だけで
 * 判定すると、**解約で宙に浮くいちばん多い形（一度も請求書が生成されて
 * いないクレジット）がボタンにも警告にも出てこない。**
 *
 * 対象はサーバー側の抽出条件と同じ
 * 「`status = 'approved'` かつ `applied_invoice_id` が空」。**`applied` を
 * 除かないと、現金で返し終えたクレジットが「未充当 1,200 円」のまま
 * 一覧と警告に残り続ける**（`applied_invoice_id` は赤伝で閉じたときにしか
 * 入らないので、返金で閉じたものは空のままになる）。
 */
export function outstandingJpy(credit: SlaCreditEntry): number {
  if (credit.status !== 'approved') return 0;
  if (credit.appliedInvoiceId !== null) return 0;
  return credit.remainingJpy > 0 ? credit.remainingJpy : credit.amountJpy;
}

export function SlaCreditsPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canWrite = usePermission(OPS_PERMISSION.billingWrite);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [settleTarget, setSettleTarget] = React.useState<SlaCreditEntry | null>(null);

  const list = useCursorList<SlaCreditEntry>({
    path: '/sla-credits',
    keyFor: (params) => qk.slaCredits(params),
    filters: { status: search.status, organizationId: search.organizationId },
  });

  const columns: Array<DataTableColumn<SlaCreditEntry>> = [
    {
      id: 'status',
      header: '状態',
      width: 'w-36',
      cell: (c) => (
        <StatusBadge
          status={c.status}
          size="sm"
          dot
          tone={toneFrom(SLA_CREDIT_STATUS_TONE, c.status)}
          label={labelFrom(SLA_CREDIT_STATUS_LABEL, c.status)}
        />
      ),
    },
    {
      id: 'org',
      header: '契約組織',
      cell: (c) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: c.organizationId }}
          search={{ tab: 'overview' }}
          className="font-medium hover:text-primary"
        >
          {c.organizationName}
        </Link>
      ),
    },
    {
      id: 'period',
      header: '対象期間',
      hideBelow: 'md',
      cell: (c) => (
        <span className="text-xs text-muted-foreground">
          {c.periodStart} 〜 {c.periodEnd}
        </span>
      ),
    },
    { id: 'amount', header: '金額', align: 'right', cell: (c) => formatJpy(c.amountJpy) },
    {
      id: 'remaining',
      header: '未充当',
      align: 'right',
      hideBelow: 'md',
      /*
       * **API の `remainingJpy` をそのまま出さない。** 0 が「まだ 1 円も
       * 充当していない」と「全額充当済み」の両方を指すため、生値を出すと
       * 承認済みで丸ごと未払いのクレジットが「0 円」に見える。
       * `outstandingJpy()` が状態と併せて解いた値を出す。
       */
      cell: (c) => {
        const left = outstandingJpy(c);
        return left > 0 ? <span className="text-warning">{formatJpy(left)}</span> : formatJpy(0);
      },
    },
    {
      id: 'rate',
      header: '返金率',
      align: 'right',
      hideBelow: 'lg',
      // 万分率で持っている（10000bp = 100%）。
      cell: (c) => (c.creditBp > 0 ? `${(c.creditBp / 100).toFixed(2)} %` : '—'),
    },
    {
      id: 'reason',
      header: '根拠',
      hideBelow: 'lg',
      cell: (c) => (
        <div className="space-y-0.5">
          <span className="block text-xs text-muted-foreground">{c.reason}</span>
          {c.incidentId ? (
            <Badge variant="muted" size="sm">
              {c.incidentId}
            </Badge>
          ) : null}
        </div>
      ),
    },
    { id: 'createdAt', header: '起票', hideBelow: 'lg', cell: (c) => formatDateTime(c.createdAt) },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-40',
      cell: (c) =>
        canWrite ? (
          <div className="flex justify-end gap-1">
            <ApproveActions credit={c} />
            {/*
              承認済みで未充当のものだけに「現金で返した」を出す。解約すると
              充当先の請求書がもう生成されないので、ここを通らないと
              `approved` のまま宙に浮き続ける（worker の critical 通知は
              `dedupeKey` で二度と来ない）。
            */}
            {c.status === 'approved' && outstandingJpy(c) > 0 ? (
              <Button variant="outline" size="sm" onClick={() => setSettleTarget(c)}>
                <Banknote aria-hidden />
                現金で返済済み
              </Button>
            ) : null}
          </div>
        ) : null,
    },
  ];

  /*
   * 宙に浮いているクレジット（承認済み・未充当）。**契約が生きていれば
   * 次の請求書で自動的に充当される**ので、ここに出るのは「まだ締めが
   * 回っていない」か「解約されて充当先が無い」のどちらか。後者は運営が
   * 現金で返すまで終わらないので、件数と合計を必ず目に入れる。
   */
  const stranded = list.rows.filter((c) => c.status === 'approved' && outstandingJpy(c) > 0);
  const strandedTotal = stranded.reduce((sum, c) => sum + outstandingJpy(c), 0);

  return (
    <>
      <PageHeader
        title="SLA クレジット"
        description="障害に伴う返金・値引きの起票と承認。承認したものが次に生成される請求書へ負の明細として載ります。"
        actions={
          <>
            <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
            {canWrite ? (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus aria-hidden />
                クレジットを起票
              </Button>
            ) : null}
          </>
        }
      />

      <FilterBar
        active={hasActiveFilter(search)}
        onClear={() => void navigate({ search: {} })}
        selects={[
          {
            key: 'status',
            label: '状態',
            value: search.status,
            options: optionsFromLabels(SLA_CREDIT_STATUS_LABEL),
            onChange: (v) =>
              void navigate({ search: (prev) => ({ ...prev, status: v as typeof search.status }) }),
          },
        ]}
      />

      {stranded.length > 0 ? (
        <Alert variant="warning" className="mb-4">
          <AlertDescription>
            承認済みで未充当のクレジットが {stranded.length} 件（合計 {formatJpy(strandedTotal)}）あります。
            契約が続いていれば次の月次締めで請求書へ自動的に載りますが、
            <strong>解約済みの契約は充当先が生成されません</strong>。
            その場合は入金への返金（請求書詳細の「返金」）か返還請求書（赤伝）で現金を返し、
            この一覧の「現金で返済済み」で閉じてください（閉じないと二重返金の危険が残ります）。
          </AlertDescription>
        </Alert>
      ) : null}

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(c) => c.id}
        loading={list.query.isLoading}
        empty={
          <ListEmpty
            filtered={hasActiveFilter(search)}
            entityLabel="SLA クレジット"
            onClearFilter={() => void navigate({ search: {} })}
          />
        }
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <CreateCreditDialog open={createOpen} onOpenChange={setCreateOpen} />
      <SettleCreditDialog credit={settleTarget} onClose={() => setSettleTarget(null)} />
    </>
  );
}

/* ------------------------------------------------- 現金返金としての締め */

/**
 * 承認済みのクレジットを `applied`（残額 0）にして閉じる
 * （`POST /ops/sla-credits/:creditId/settle`）。
 *
 * **この画面は返金そのものを行わない。** 返金は入金への返金
 * （請求書詳細の「返金」）か返還請求書（赤伝）で先に済ませ、ここでは
 * 「返し終えた」事実だけを記録する。根拠の ID を必須にしているのは
 * サーバー側の仕様で、監査ログから現金の動きへ辿れない「閉じただけ」の
 * 行を作らせないため（別組織の入金 / 請求書は 400 で弾かれる）。
 */
function SettleCreditDialog({
  credit,
  onClose,
}: {
  credit: SlaCreditEntry | null;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const [method, setMethod] = React.useState<'refund' | 'credit_note'>('refund');
  const [reference, setReference] = React.useState('');
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    setMethod('refund');
    setReference('');
    setNote('');
  }, [credit?.id]);

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; settledAmount: number }>(`/sla-credits/${credit?.id ?? ''}/settle`, {
        method,
        // 根拠はどちらか一方だけを送る（`.strict()` なので余計なキーは 400）。
        ...(method === 'refund' ? { paymentId: reference.trim() } : { invoiceId: reference.trim() }),
        note: note.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'sla-credits'] });
      onClose();
    },
  });

  if (!credit) return null;

  return (
    <FormDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="現金で返済済みとして閉じる"
      description={`${credit.organizationName} の未充当 ${formatJpy(outstandingJpy(credit))} を「返済済み」として閉じます。閉じたクレジットは二度と充当されません。`}
      submitLabel="返済済みにする"
      pending={submit.isPending}
      error={submit.isError ? submit.error : undefined}
      disabled={reference.trim().length === 0 || note.trim().length === 0}
      onSubmit={() => submit.mutate()}
    >
      <Alert variant="warning">
        <AlertDescription>
          <strong>先に返金を済ませてください。</strong>
          この操作はクレジットを閉じるだけで、お金は動きません。順序を逆にすると
          「閉じたのに返していない」状態になり、契約者からは請求だけが残ります。
        </AlertDescription>
      </Alert>

      <Field label="返し方" required>
        <NativeSelect
          value={method}
          onChange={(v) => {
            setMethod(v as 'refund' | 'credit_note');
            setReference('');
          }}
          options={[
            { value: 'refund', label: '入金への返金（請求書詳細の「返金」で実施済み）' },
            { value: 'credit_note', label: '返還請求書（赤伝）を交付済み' },
          ]}
        />
      </Field>

      <Field
        label={method === 'refund' ? '返金した入金の ID' : '返還請求書（または元の請求書）の ID'}
        required
        hint="同じ契約組織のものでなければサーバーが 400 で弾きます。請求書詳細の支払い履歴からコピーしてください。"
      >
        <Input
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          spellCheck={false}
          className="font-mono"
        />
      </Field>

      <Field label="メモ" required hint="いつ・どのように返したか。監査ログに残ります。">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
      </Field>
    </FormDialog>
  );
}

/* --------------------------------------------------------------- 承認 */

function ApproveActions({ credit }: { credit: SlaCreditEntry }): React.ReactElement | null {
  const qc = useQueryClient();
  const decide = useMutation({
    mutationFn: (approve: boolean) => api.post(`/sla-credits/${credit.id}/approve`, { approve }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ops', 'sla-credits'] }),
  });

  // 承認・却下できるのは `proposed` だけ。`applied` は請求書へ反映済みで変更できない。
  if (credit.status !== 'proposed') return null;

  return (
    <div className="flex justify-end gap-1">
      <Button variant="outline" size="sm" onClick={() => decide.mutate(true)} loading={decide.isPending}>
        <Check aria-hidden />
        承認
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="却下する"
        onClick={() => decide.mutate(false)}
        disabled={decide.isPending}
      >
        <X aria-hidden />
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------- 起票 */

function CreateCreditDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [organizationId, setOrganizationId] = React.useState('');
  const [periodStart, setPeriodStart] = React.useState('');
  const [periodEnd, setPeriodEnd] = React.useState('');
  /** `amount`（円）か `creditBp`（率）のどちらかで指定する。 */
  const [mode, setMode] = React.useState<'amount' | 'rate'>('amount');
  const [amount, setAmount] = React.useState('');
  const [ratePercent, setRatePercent] = React.useState('');
  const [incidentId, setIncidentId] = React.useState('');
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setOrganizationId('');
    setPeriodStart('');
    setPeriodEnd('');
    setMode('amount');
    setAmount('');
    setRatePercent('');
    setIncidentId('');
    setReason('');
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      api.post('/sla-credits', {
        organizationId: organizationId.trim(),
        periodStart,
        periodEnd,
        // 率で指定したときはサーバーが「その組織の現在の月額 × 率」で計算する。
        // 画面で先に計算して amount で送ると、単価改定のたびに画面側の計算が古くなる。
        ...(mode === 'amount'
          ? { amount: Number(amount) }
          : { creditBp: Math.round(Number(ratePercent) * 100) }),
        ...(incidentId.trim().length > 0 ? { incidentId: incidentId.trim() } : {}),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'sla-credits'] });
      onOpenChange(false);
    },
  });

  // サーバー側の zod と同じ条件（期間は `yyyy-MM-dd`・終了が開始以降・理由必須）。
  const dateOk = (v: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(v);
  const invalid =
    organizationId.trim().length === 0 ||
    !dateOk(periodStart) ||
    !dateOk(periodEnd) ||
    periodEnd < periodStart ||
    reason.trim().length === 0 ||
    (mode === 'amount' ? !(Number(amount) > 0) : !(Number(ratePercent) > 0));

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="SLA クレジットを起票する"
      description="承認待ち（proposed）として作られます。承認するまで請求書には載りません。"
      submitLabel="起票する"
      pending={submit.isPending}
      error={submit.error}
      disabled={invalid}
      onSubmit={() => submit.mutate()}
      wide
    >
      <Field label="契約組織 ID" required hint="契約組織の一覧からコピーします。">
        <Input
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="対象期間の開始" required>
          <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
        </Field>
        <Field label="対象期間の終了" required>
          <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
        </Field>
      </div>

      <Field label="金額の決め方" required>
        <NativeSelect
          value={mode}
          onChange={(v) => setMode(v as 'amount' | 'rate')}
          options={[
            { value: 'amount', label: '金額を直接指定する' },
            { value: 'rate', label: '月額に対する率で指定する' },
          ]}
        />
      </Field>

      {mode === 'amount' ? (
        <Field label="金額（円）" required>
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
          />
        </Field>
      ) : (
        <Field label="返金率（%）" required hint="現在の月額（単価 × 枠数）に対する率。金額はサーバーが計算します。">
          <Input
            value={ratePercent}
            onChange={(e) => setRatePercent(e.target.value.replace(/[^0-9.]/g, ''))}
            inputMode="decimal"
            placeholder="10"
          />
        </Field>
      )}

      <Field label="インシデント ID" hint="任意。障害インシデントの画面からコピーします。">
        <Input value={incidentId} onChange={(e) => setIncidentId(e.target.value)} spellCheck={false} />
      </Field>

      <Field label="根拠" required hint="監査ログと承認者の判断材料になります。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="INC-2026-0007 による 4 時間の受信停止に対する補償（SLA 99.9% 未達）"
        />
      </Field>

      <Alert>
        <AlertDescription>
          起票と承認は別の操作です。承認は<strong>起票者以外</strong>が行ってください（金額が請求書へ直接効きます）。
        </AlertDescription>
      </Alert>
    </FormDialog>
  );
}
